// src/pages/MoniepointPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

const MoniepointPage = () => {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [balance, setBalance] = useState(0);
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🎨 ORIGINAL THEME — Corporate dashboard style (different from #1, #2, #3)
  const theme = isGhana
    ? {
        bgColor: "bg-[#0a1410]", // deep green-black for Ghana
        balanceGradient: "from-[#0d3b1f] to-[#0a1410]",
        balanceBorder: "border-emerald-500/30",
        sendBtn: "bg-emerald-600 hover:bg-emerald-700",
        addBtn: "bg-white/10 hover:bg-white/20",
        statColor: "text-emerald-300",
        cardBg: "bg-white/5",
        cardBorder: "border-white/10",
        bottomBarBg: "bg-emerald-950/80",
        currencySymbol: config.currencySymbol,
        walletName: "GhanaCorp Wallet",
        flag: "🇬🇭",
      }
    : {
        bgColor: "bg-[#0b1220]", // deep navy for Nigeria
        balanceGradient: "from-[#1b2a4a] to-[#111827]",
        balanceBorder: "border-blue-500/20",
        sendBtn: "bg-blue-600 hover:bg-blue-700",
        addBtn: "bg-white/10 hover:bg-white/20",
        statColor: "text-blue-300",
        cardBg: "bg-white/5",
        cardBorder: "border-white/10",
        bottomBarBg: "bg-black/60",
        currencySymbol: config.currencySymbol,
        walletName: "ProBank Business",
        flag: "🇳🇬",
      };

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        const res = await API.get("/dashboard");
        setBalance(res.data?.balances?.account || res.data?.user?.balance || 0);
        setUser(res.data?.user || null);

        const txRes = await API.get("/bank/wallet/history");
        setTransactions(txRes.data?.transactions || []);
      } catch (err) {
        console.error("Error loading wallet data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWalletData();
  }, []);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme.bgColor} text-white`}>
        <div className="animate-pulse text-sm text-gray-300">Loading wallet...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bgColor} text-white pb-24`}>

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      {/* TOP HEADER */}
      <div className="px-5 pt-6 pb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Hello, {user?.name || "User"}</h1>
          <p className="text-xs text-gray-400">{theme.walletName} {theme.flag}</p>
        </div>

        <div className="flex gap-3 text-xl">
          <button className="hover:opacity-80 transition">🔔</button>
          <button className="hover:opacity-80 transition">⚙️</button>
        </div>
      </div>

      {/* BALANCE CARD - Corporate style with subtle gradient.
          Send opens the branded selector (identity: "moniepoint"). */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`mx-5 bg-gradient-to-r ${theme.balanceGradient} border ${theme.balanceBorder} rounded-2xl p-5 shadow-lg`}
      >
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-gray-300 uppercase tracking-widest">Available Balance</p>
          <p className="text-[10px] text-gray-400 font-mono">ACC-{user?._id?.slice(-6).toUpperCase() || "000000"}</p>
        </div>

        <h2 className="text-3xl font-bold mt-2 tracking-tight">
          {theme.currencySymbol}{Number(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </h2>

        <div className="mt-5 flex gap-3">
          <button
            onClick={() => navigate("/transfer-select", { state: { from: "moniepoint" } })}
            className={`flex-1 ${theme.sendBtn} transition py-2.5 rounded-xl text-sm font-semibold`}
          >
            Send
          </button>

          <button
            onClick={() => navigate("/purchase")}
            className={`flex-1 ${theme.addBtn} transition py-2.5 rounded-xl text-sm`}
          >
            Add Money
          </button>
        </div>
      </motion.div>

      {/* CORPORATE STATS GRID — unique to this wallet */}
      <div className="mx-5 mt-5 grid grid-cols-2 gap-3">
        <div className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-3`}>
          <p className="text-[10px] text-gray-400 uppercase tracking-widest">Inflow</p>
          <p className="text-base font-bold text-green-400 mt-1">+{theme.currencySymbol}0.00</p>
          <p className="text-[10px] text-gray-500 mt-0.5">This week</p>
        </div>
        <div className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-3`}>
          <p className="text-[10px] text-gray-400 uppercase tracking-widest">Outflow</p>
          <p className="text-base font-bold text-red-400 mt-1">-{theme.currencySymbol}0.00</p>
          <p className="text-[10px] text-gray-500 mt-0.5">This week</p>
        </div>
      </div>

      {/* QUICK ACTIONS — Wallet and Bank both open the branded selector */}
      <div className="mx-5 mt-5 grid grid-cols-3 gap-3">
        <button
          onClick={() => navigate("/transfer-select", { state: { from: "moniepoint" } })}
          className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-4 text-center hover:bg-white/10 transition flex flex-col items-center gap-1`}
        >
          <span className="text-xl">💼</span>
          <p className="text-xs">Wallet</p>
        </button>

        <button
          onClick={() => navigate("/transfer-select", { state: { from: "moniepoint" } })}
          className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-4 text-center hover:bg-white/10 transition flex flex-col items-center gap-1`}
        >
          <span className="text-xl">🏛️</span>
          <p className="text-xs">Bank</p>
        </button>

        <button className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-4 text-center opacity-50 flex flex-col items-center gap-1`}>
          <span className="text-xl">💸</span>
          <p className="text-xs">Withdraw</p>
        </button>
      </div>

      {/* BUSINESS TOOLS SECTION (unique to corporate style) */}
      <div className="mx-5 mt-5">
        <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">Business Tools</p>
        <div className="grid grid-cols-4 gap-3">
          {[
            { icon: "📊", label: "Reports" },
            { icon: "🧾", label: "Invoices" },
            { icon: "💳", label: "POS" },
            { icon: "📈", label: "Insights" },
          ].map((item, i) => (
            <button
              key={i}
              className={`${theme.cardBg} border ${theme.cardBorder} rounded-xl p-3 flex flex-col items-center gap-1 hover:bg-white/10 transition`}
            >
              <span className="text-xl">{item.icon}</span>
              <p className="text-[10px] text-gray-300">{item.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* TRANSACTIONS - Corporate table style */}
      <div className={`mx-5 mt-6 ${theme.cardBg} border ${theme.cardBorder} rounded-2xl overflow-hidden`}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-semibold text-sm">Recent Transactions</h3>
          <button className={`text-xs ${theme.statColor}`}>View All ›</button>
        </div>

        {Array.isArray(transactions) && transactions.length > 0 ? (
          transactions.map((tx, idx) => (
            <div
              key={idx}
              className="flex justify-between items-center px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition"
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg ${theme.cardBg} border ${theme.cardBorder} flex items-center justify-center text-xs`}>
                  {tx?.type === "credit" ? "↓" : "↑"}
                </div>
                <div>
                  <p className="text-sm">{tx?.title || "Transaction"}</p>
                  <p className="text-[11px] text-gray-400">
                    {tx?.createdAt ? new Date(tx.createdAt).toLocaleString() : ""}
                  </p>
                </div>
              </div>

              <div
                className={`text-sm font-semibold ${
                  tx?.status === "failed"
                    ? "text-red-400"
                    : tx?.status === "confirmed"
                    ? "text-green-400"
                    : "text-yellow-300"
                }`}
              >
                {theme.currencySymbol}{Number(tx?.amount || 0).toLocaleString()}
              </div>
            </div>
          ))
        ) : (
          <div className="p-6 text-center text-gray-400 text-sm">No transactions yet</div>
        )}
      </div>

      {/* FLOATING BOTTOM BAR — Transfer opens the branded selector */}
      <div className="fixed bottom-5 left-0 right-0 flex justify-center px-4">
        <div className={`${theme.bottomBarBg} backdrop-blur border border-white/10 px-6 py-3 rounded-full flex gap-6 text-xs shadow-2xl`}>
          <button onClick={() => navigate("/dashboard")} className="hover:opacity-80 transition">Home</button>
          <button onClick={() => navigate("/transfer-select", { state: { from: "moniepoint" } })} className="hover:opacity-80 transition">Transfer</button>
          <button onClick={() => navigate("/purchase")} className="hover:opacity-80 transition">Add</button>
        </div>
      </div>
    </div>
  );
};

export default MoniepointPage;
